import React, { useState, useEffect, useRef } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Button, Spinner, Modal, ModalHeader, ModalBody,
    Form, FormGroup, Label, Input
} from 'reactstrap';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import Swal from 'react-bootstrap-sweetalert';
import AWS from 'aws-sdk';
import './virtualGifts.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL?.replace(/\/$/, '');

const SortableItem = ({ gift, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: gift._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="col-xl-3 col-lg-4 col-md-6 mb-3">
            <Card className="gift-card">
                <div className="drag-handle p-2 text-center bg-light" {...attributes} {...listeners}>
                    <i className="ri-drag-move-2-fill"></i>
                </div>
                <CardBody>
                    <div className="text-center">
                        <img
                            src={gift.iconUrl.startsWith('http') ? gift.iconUrl : `${MEDIA_URL}/${gift.iconUrl.replace(/^\//, '')}`}
                            alt={gift.title}
                            className="gift-image mb-3"
                            style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'contain'
                            }}
                        />
                        <h5 className="mb-1">{gift.title}</h5>
                        <p className="text-muted">
                            {gift.valueInTokens} Tokens
                        </p>
                        <div className="hstack gap-2 justify-content-center">
                            <Button
                                color="soft-primary"
                                size="sm"
                                onClick={() => onEdit(gift)}
                            >
                                <i className="ri-pencil-line"></i>
                            </Button>
                            <Button
                                color="soft-danger"
                                size="sm"
                                onClick={() => onDelete(gift._id)}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </Button>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

const VirtualGifts = () => {
    document.title = "Virtual Gifts";
    
    const [gifts, setGifts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [selectedGift, setSelectedGift] = useState(null);
    const [sweetAlert, setSweetAlert] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const [cognitoData, setCognitoData] = useState(null);
    const [awsCredentials, setAwsCredentials] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchGifts = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/virtualGift`, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });

            const data = await response.json();
            if (data.data) {
                setGifts(data.data);
            }
        } catch (error) {
            console.error('Error fetching gifts:', error);
            toast.error("Failed to fetch virtual gifts");
            toast.error(error.message || 'Failed to fetch virtual gifts', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGifts();
    }, []);

    const getImageUrl = (url) => {
        if (!url) return '/images/default-gift.png';
        if (url.startsWith('http') || url.startsWith('//')) return url;
        return `${MEDIA_URL}/${url.replace(/^\//, '')}`;
    };

    const handleEdit = async (gift) => {
        setSelectedGift(gift);
        
        // Clear file input and preview if adding new gift
        if (!gift) {
            setSelectedFile(null);
            setImagePreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } else if (gift?.iconUrl) {
            setImagePreview(getImageUrl(gift.iconUrl));
        }
        
        try {
            // Get Cognito Token
            const response = await fetch(`${API_BASE_URL}/cognitoToken`, {
                method: 'GET',
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });
            
            const data = await response.json();
            if (data.data) {
                setCognitoData(data.data);
            }
        } catch (error) {
            console.error('Error fetching cognito token:', error);
            toast.error('Failed to initialize upload configuration', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        }
        
        setEditModal(true);
    };

    const handleDelete = (giftId) => {
        setSweetAlert(
            <Swal
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                title="Are you sure?"
                onConfirm={async () => {
                    setSweetAlert(null);
                    try {
                        setLoading(true);
                        const response = await fetch(`${API_BASE_URL}/virtualGift`, {
                            method: 'DELETE',
                            headers: {
                                'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                virtualGiftId: giftId
                            })
                        });

                        const data = await response.json();

                        if (data.message === "Success") {
                            toast.success("Gift deleted successfully");
                            fetchGifts(); // Refresh the list
                        } else {
                            //throw new Error(data.message || 'Failed to delete gift');
                            toast.error(data.message || 'Failed to delete gift', {
                                position: "top-right",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                            });
                        }
                    } catch (error) {
                        console.error('Error deleting gift:', error);
                        toast.error(error.message || 'Failed to delete gift', {
                            position: "top-right",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                        });
                    } finally {
                        setLoading(false);
                    }
                }}
                onCancel={() => setSweetAlert(null)}
                focusCancelBtn
            >
                You won't be able to revert this!
            </Swal>
        );
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setGifts((items) => {
                const oldIndex = items.findIndex(item => item._id === active.id);
                const newIndex = items.findIndex(item => item._id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // File validation
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            throw new Error('Please upload an image file');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size should be less than 2MB', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            throw new Error('File size should be less than 2MB');
            return;
        }

        try {
            setLoading(true);

            // 1. Get Cognito Token
            const cognitoResponse = await fetch(`${API_BASE_URL}/cognitoToken`, {
                method: 'GET',
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });
            
            const cognitoData = await cognitoResponse.json();
            if (!cognitoData.data) {
                toast.error('Failed to get upload configuration', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
                throw new Error('Failed to get upload configuration');
            }

            // 2. Configure AWS
            AWS.config.update({
                region: cognitoData.data.region,
                credentials: new AWS.CognitoIdentityCredentials({
                    IdentityId: cognitoData.data.IdentityId,
                    Logins: {
                        'cognito-identity.amazonaws.com': cognitoData.data.Token
                    }
                })
            });

            // Wait for credentials to be refreshed
            await new Promise((resolve, reject) => {
                AWS.config.credentials.refresh((error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve();
                    }
                });
            });

            // 3. Create S3 instance with credentials
            const s3 = new AWS.S3({
                apiVersion: '2006-03-01',
                params: { Bucket: cognitoData.data.imageBucket },
                region: cognitoData.data.region
            });

            // 4. Upload to S3
            const uploadParams = {
                Bucket: cognitoData.data.imageBucket,
                Key: `image/${file.name}`,
                Body: file,
                ContentType: file.type,
                ACL: 'public-read'
            };

            const uploadResult = await s3.upload(uploadParams).promise();

            // Update state with the uploaded file info
            setSelectedFile(file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

        } catch (error) {
            toast.error(error.message || 'Failed to upload image', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('drag-over');
        const file = event.dataTransfer.files[0];
        if (file) {
            fileInputRef.current.files = event.dataTransfer.files;
            handleFileSelect({ target: { files: [file] } });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData(event.target);
            const title = formData.get('title');
            const valueInTokens = formData.get('valueInTokens');

            if (!title || !valueInTokens) {
                toast.error('Please fill in all required fields', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
                return;
            }

            const payload = {
                title: {
                    en: title // title in English
                },
                valueInTokens: Number(valueInTokens),
                iconUrl: selectedFile ? `image/${selectedFile.name}` : selectedGift?.iconUrl,
                virtualGiftId: selectedGift?._id // Include gift ID for updates
            };

            const response = await fetch(`${API_BASE_URL}/virtualGift`, {
                method: 'PATCH',
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.message === "Success") {
                toast.success(`Gift ${selectedGift ? 'updated' : 'added'} successfully`);
                setEditModal(false);
                fetchGifts();
            } else {
                toast.error(data.message || 'Something went wrong', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
                throw new Error(data.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('Error submitting gift:', error);
            toast.error(error.message || 'Failed to save gift', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
        <div className="page-content">
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
                
            <Container fluid>
                    <BreadCrumb title="Virtual Gifts" pageTitle="Settings" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center">
                                    <h5 className="card-title flex-grow-1 mb-0">Virtual Gifts</h5>
                                    <Button color="primary" onClick={() => handleEdit(null)}>
                                        <i className="ri-add-line align-bottom me-1"></i> Add Gift
                                    </Button>
                            </CardHeader>
                            <CardBody>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext
                                            items={gifts.map(g => g._id)}
                                            strategy={rectSortingStrategy}
                                        >
                                            <div className="row">
                                                {gifts.map((gift) => (
                                                    <SortableItem
                                                        key={gift._id}
                                                        gift={gift}
                                                        onEdit={handleEdit}
                                                        onDelete={handleDelete}
                                                    />
                                                ))}
                                </div>
                                        </SortableContext>
                                    </DndContext>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            </div>

            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}

            <Modal
                isOpen={editModal}
                toggle={() => setEditModal(false)}
                size="md"
            >
                <ModalHeader toggle={() => setEditModal(false)}>
                    {selectedGift ? 'Edit Gift' : 'Add New Gift'}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label for="title">Title</Label>
                            <Input
                                type="text"
                                name="title"
                                id="title"
                                placeholder="Enter gift title"
                                defaultValue={selectedGift?.title}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="valueInTokens">Value in Tokens</Label>
                            <Input
                                type="number"
                                name="valueInTokens"
                                id="valueInTokens"
                                placeholder="Enter token value"
                                defaultValue={selectedGift?.valueInTokens}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Gift Image</Label>
                            <div className="upload-zone p-3 text-center border rounded">
                                {selectedGift?.iconUrl && !imagePreview && (
                                    <div className="current-image mb-3">
                                        <img
                                            src={getImageUrl(selectedGift.iconUrl)}
                                            alt="Current"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '150px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                        <p className="text-muted small mt-2">Current image</p>
                                </div>
                            )}
                                <div
                                    className="upload-area"
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: '#f8f9fa',
                                        transition: 'all 0.3s ease',
                                        padding: '1.5rem',
                                        border: '2px dashed #dee2e6'
                                    }}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    {imagePreview ? (
                                        <div className="preview-container">
                                            <div className="image-preview-wrapper position-relative">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '150px',
                                                        objectFit: 'contain',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedFile(null);
                                                        setImagePreview(null);
                                                        fileInputRef.current.value = '';
                                                    }}
                                                >
                                                    <i className="ri-close-line"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <i className="ri-upload-cloud-2-line display-4 text-muted"></i>
                                            <p className="mb-0">Click or drag to upload new image</p>
                                            <p className="text-muted small mb-0">
                                                Supported formats: PNG, JPG, JPEG
                                            </p>
                                        </>
                                    )}
                                    <Input
                                        type="file"
                                        innerRef={fileInputRef}
                                        className="d-none"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                    />
                                        </div>
                            </div>
                        </FormGroup>
                        <div className="text-end mt-4">
                            <Button type="button" color="light" className="me-2" onClick={() => setEditModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" color="primary">
                                {selectedGift ? 'Update' : 'Add'} Gift
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
            {sweetAlert}
        </React.Fragment>
    );
};

export default VirtualGifts;